import {
  getHoursFromTimeString,
  getMinutesFromTimeString,
  weekDays,
} from "@infrastructure/Utils";
import { Text } from "react-native-paper";
import { theme } from "@config/Theme";
import { StyleSheet, View } from "react-native";
import { ScheduleViewModel } from "@viewModels/Schedule.viewModel";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";

interface Schedule {
  dayOfWeek: number;
  open: string;
  close: string;
  isHoliday: boolean;
}

interface Props {
  schedule?: Array<ScheduleViewModel> | null;
  forDetails?: boolean;
}

// Note:
// FROM BE side we get dayOfWeek in the response of each object
// dayOfWeek = 0 --> it refres to monday
// date.getDay() ---> 1 it refres to monday
function getScheduleForSpecificDate(
  schedules: Schedule[],
  date: Date
): Schedule {
  const day = date.getDay();
  const year = date.getFullYear();
  const month = date.getMonth();
  const filteredSchedules = schedules.filter((s) => s.dayOfWeek + 1 === day);

  if (filteredSchedules.length > 0) {
    // in case : there are schedules for today
    // we have more than one case here :
    // 1- if we have a schedule for today and the current date in the schedule range(kp is opened) (best choice)
    // 2- if we have a schedule for today but in future (will use it if term 1 is not found)
    // 3- otherwise we will check next days

    const result = filteredSchedules.find((it) => {
      const dates = getOpeningAndClosingDate(it);
      dates.openingDate.setFullYear(year, month);
      dates.closingDate.setFullYear(year, month);

      // first part : once kp is opened and currentDate is in the kp date range (date >= dates.openingDate && date <= dates.closingDate)
      // second part : future schedule for today (dates.openingDate > date)
      // otherwise : check the other schedules
      if (
        !it.isHoliday &&
        ((date >= dates.openingDate && date <= dates.closingDate) ||
          dates.openingDate > date)
      ) {
        return it;
      }
    });

    return result ?? findNextScedule(schedules);
  } else {
    return findNextScedule(schedules);
  }
}

function getOpeningAndClosingDate(schedule: Schedule) {
  const openingHours = getHoursFromTimeString(schedule.open);
  const openingMinutes = getMinutesFromTimeString(schedule.open);
  const closingingHours = getHoursFromTimeString(schedule.close);
  const closingMinutes = getMinutesFromTimeString(schedule.close);
  const openingDate = new Date();
  const closingDate = new Date();

  openingDate.setHours(openingHours);
  openingDate.setMinutes(openingMinutes);

  closingDate.setHours(closingingHours);
  closingDate.setMinutes(closingMinutes);

  return {
    openingDate: openingDate,
    closingDate: closingDate,
  };
}
/**
 * - in case we are in a specific day and this day has schedules
 * - but the current date is not in the schedule time range
 */
function findClosestSchedule(schedules: Schedule[]) {
  const schedulesLength = schedules.length;
  let closestSchedule = schedules[0];
  if (schedulesLength === 1) {
    return closestSchedule;
  }

  for (let index = 0; index < schedulesLength; index++) {
    const date = getOpeningAndClosingDate(schedules[index]).openingDate;
    const date1 = getOpeningAndClosingDate(closestSchedule).openingDate;
    closestSchedule = date < date1 ? schedules[index] : closestSchedule;
  }

  return closestSchedule;
}

/**
 * - in case we are in a specific day and this day has no available schedules
 * - we will check other days
 */
function findNextScedule(schedules: Schedule[]) {
  let dayOffset = 1;
  let nextSchedule: Schedule | null = null;
  const currentDate = new Date();
  while (nextSchedule === null) {
    const day = (currentDate.getDay() + dayOffset) % 7;
    const nextDaySchedules = schedules.filter(
      (s) => (s.dayOfWeek + 1) % 7 === day
    );

    if (nextDaySchedules.length === 0) {
      dayOffset++;
    } else {
      const isHolidaySchedule = nextDaySchedules.every((s) => s.isHoliday);
      if (isHolidaySchedule) {
        dayOffset++;
      } else {
        const nonHolidaySchedules = nextDaySchedules.filter(
          (s) => !s.isHoliday
        );

        nextSchedule = findClosestSchedule(nonHolidaySchedules);
      }
    }
  }

  return nextSchedule;
}

export const TodaySchedule = ({ schedule, forDetails = false }: Props) => {
  const { t } = useTranslation("common");
  const [isOpened, setIsOpened] = useState<boolean>(false);
  const [scheduleAsResult, setScheduleAsResult] = useState<Schedule | null>(
    null
  );

  if (!schedule || schedule.length === 0) {
    return null;
  }

  const currentDate = new Date();

  useEffect(() => {
    const result = getScheduleForSpecificDate(schedule, currentDate);
    setScheduleAsResult(result);
  }, []);

  // check if the kp opened
  useEffect(() => {
    if (!scheduleAsResult) return;
    if (currentDate.getDay() === scheduleAsResult.dayOfWeek + 1) {
      const dates = getOpeningAndClosingDate(scheduleAsResult);
      const isOpen =
        currentDate >= dates.openingDate && currentDate <= dates.closingDate;
      setIsOpened(isOpen);
    } else {
      setIsOpened(false);
    }
  }, [scheduleAsResult]);

  const fontSize = forDetails ? 16 : 12;

  return (
    <View style={styles.container}>
      <Text style={[{ fontSize }, isOpened ? styles.open : styles.close]}>
        {isOpened ? t("todaySchedule_opened") : t("todaySchedule_closed")}
      </Text>
      {!isOpened && <Text>-</Text>}
      <Text style={{ fontSize, flex: 1 }}>
        {isOpened
          ? ""
          : t("kp_is_closed_will_open_at", {
              day: scheduleAsResult
                ? t(weekDays[scheduleAsResult.dayOfWeek])
                : "",
              openingTime: scheduleAsResult?.open ?? "",
            })}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: 5,
  },
  open: {
    color: theme.colors.success,
  },
  close: {
    color: theme.colors.error,
  },
});
